package org.example.project.enginekalender

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import org.example.project.dashboard.DashboardTheme
import org.example.project.service.GlobalManager
import org.example.project.utils.All
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.events.MouseEvent
import org.w3c.dom.TouchEvent
import kotlin.math.abs
import kotlin.math.roundToInt

@Composable
fun Monatsübersicht(
    selectedDate: String,
    art: String,
    kalenderUserId: String,
    kalenderFirmenId: String = "",
    bookingsOverride: List<All>? = null,
    showBookingHover: Boolean = true,
    showPrivateBookingDetails: Boolean = true,
    dayTextByDate: Map<String, String> = emptyMap(),
    dayTextColorByDate: Map<String, String> = emptyMap(),
    dayMarkerColorByDate: Map<String, String> = emptyMap(),
    dayMarkerTextByDate: Map<String, String> = emptyMap(),
    highlightedDates: Set<String> = emptySet(),
    onSelectDate: (String) -> Unit
) {
    val kalenderLiveVersion = GlobalManager.jsonDomainVersion("kalender")
    val monthData = remember(selectedDate) { buildKalenderMonthData(selectedDate, selectedDate) }
    var loadedBookings by remember(art, kalenderUserId, kalenderFirmenId) { mutableStateOf(emptyList<All>()) }
    val bookings = bookingsOverride ?: loadedBookings
    var hoverDay by remember { mutableStateOf<All?>(null) }
    var hoverBookings by remember { mutableStateOf(emptyList<All>()) }
    var hoverX by remember { mutableStateOf(0.0) }
    var hoverY by remember { mutableStateOf(0.0) }
    var swipeStartX by remember { mutableStateOf<Double?>(null) }
    var swipeConsumed by remember { mutableStateOf(false) }
    val dayStatesByDate = remember(bookings) {
        bookings.groupBy { it.datum }.mapValues { (_, dayBookings) -> kalenderDayState(dayBookings) }
    }

    fun switchMonth(delta: Int) {
        hoverDay = null
        hoverBookings = emptyList()
        onSelectDate(monatsübersichtMonthIso(selectedDate, delta))
    }

    fun finishSwipe(clientX: Double) {
        val startX = swipeStartX ?: return
        val deltaX = clientX - startX
        swipeStartX = null
        if (abs(deltaX) <= 80.0) return
        swipeConsumed = true
        if (deltaX < 0) switchMonth(1) else switchMonth(-1)
    }

    LaunchedEffect(art, kalenderUserId, kalenderFirmenId, kalenderLiveVersion) {
        if (bookingsOverride != null) return@LaunchedEffect
        loadedBookings = if (kalenderFirmenId.isNotBlank()) {
            loadScopedKalenderBookings(kalenderFirmenId, kalenderUserId)
        } else {
            loadKalenderBookings(art, kalenderUserId)
        }
    }

    Div({
        style {
            width(100.percent)
            flexGrow(1)
            flexShrink(1)
            minWidth(0.px)
            minHeight(0.px)
            height(100.percent)
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            alignItems(AlignItems.Center)
            boxSizing("border-box")
        }
    }) {
        Div({
                style {
                    width(100.percent)
                    maxWidth(100.percent)
                    height(100.percent)
                minHeight(0.px)
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                boxSizing("border-box")
            }
        }) {
            Div({
                style {
                    width(100.percent)
                    height(100.percent)
                    minHeight(0.px)
                    property("background", "rgba(255,255,255,0.035)")
                    property("border", "1px solid rgba(255,255,255,0.14)")
                    borderRadius(8.px)
                    overflow("hidden")
                    boxSizing("border-box")
                }
            }) {
            Div({
                style {
                    display(DisplayStyle.Grid)
                    gridTemplateColumns("repeat(7, minmax(0, 1fr))")
                    backgroundColor(Color("rgba(255,255,255,0.025)"))
                    property("border-bottom", "1px solid rgba(255,255,255,0.14)")
                }
            }) {
                (0..6).forEach { index ->
                    Div({
                        style {
                            minWidth(0.px)
                            padding(12.px, 10.px)
                            property("border-left", if (index == 0) "0" else "1px solid rgba(255,255,255,0.14)")
                            boxSizing("border-box")
                            textAlign("center")
                        }
                    }) {
                        Span({
                            style {
                                color(DashboardTheme.TEXT_LIGHT)
                                fontSize(15.px)
                                fontWeight("800")
                            }
                        }) { Text(kalenderWeekdayShortName(index)) }
                    }
                }
            }

            Div({
                style {
                    display(DisplayStyle.Grid)
                    gridTemplateColumns("repeat(7, minmax(0, 1fr))")
                    property("grid-template-rows", "repeat(6, minmax(0, 1fr))")
                    property("height", "calc(100% - 44px)")
                    minHeight(0.px)
                }
                onMouseDown { swipeStartX = it.clientX.toDouble() }
                onMouseUp { finishSwipe(it.clientX.toDouble()) }
                onMouseLeave { swipeStartX = null }
                onTouchStart {
                    val touch = it.touches.item(0) ?: return@onTouchStart
                    swipeStartX = touch.clientX.toDouble()
                }
                onTouchEnd {
                    val touchEvent = it.nativeEvent as? TouchEvent ?: return@onTouchEnd
                    val touch = touchEvent.changedTouches.item(0) ?: return@onTouchEnd
                    finishSwipe(touch.clientX.toDouble())
                }
            }) {
                monthData.module.forEachIndexed { index, day ->
                    val dayState = dayStatesByDate[day.datum] ?: kalenderDayState(emptyList())
                    val isSelected = day.datum == selectedDate
                    val isToday = day.datum == kalenderTodayIsoDate()
                    Div({
                        style {
                            minWidth(0.px)
                            padding(12.px)
                            property("border-left", if (index % 7 == 0) "0" else "1px solid rgba(255,255,255,0.10)")
                            property("border-top", if (index < 7) "0" else "1px solid rgba(255,255,255,0.10)")
                            if (isToday) {
                                property("box-shadow", "inset 0 0 0 2px #ff2f46")
                            }
                            backgroundColor(
                                when {
                                    day.datum in highlightedDates -> Color("rgba(87,145,225,0.35)")
                                    isSelected || isToday -> Color("rgba(255,255,255,0.055)")
                                    !day.aktiv -> Color("rgba(255,255,255,0.012)")
                                    else -> Color.transparent
                                }
                            )
                            cursor("pointer")
                            boxSizing("border-box")
                            overflow("hidden")
                            position(Position.Relative)
                        }
                        onClick {
                            if (swipeConsumed) {
                                swipeConsumed = false
                            } else {
                                onSelectDate(day.datum)
                            }
                        }
                        if (showBookingHover) {
                            onMouseEnter { event ->
                                val mouseEvent = event.nativeEvent as? MouseEvent
                                hoverDay = day
                                hoverBookings = dayState.bookings.sortedBy { it.uhrzeitVon }
                                hoverX = mouseEvent?.clientX?.toDouble() ?: 0.0
                                hoverY = mouseEvent?.clientY?.toDouble() ?: 0.0
                            }
                            onMouseMove { event ->
                                val mouseEvent = event.nativeEvent as? MouseEvent
                                hoverX = mouseEvent?.clientX?.toDouble() ?: hoverX
                                hoverY = mouseEvent?.clientY?.toDouble() ?: hoverY
                            }
                            onMouseLeave {
                                hoverDay = null
                                hoverBookings = emptyList()
                            }
                        }
                    }) {
                        Div({
                            style {
                                display(DisplayStyle.Flex)
                                alignItems(AlignItems.Center)
                                justifyContent(JustifyContent.FlexStart)
                                property("white-space", "nowrap")
                            }
                        }) {
                            Span({
                                style {
                                    color(
                                        when {
                                            isToday -> Color("#ff2f46")
                                            day.aktiv -> DashboardTheme.TEXT_LIGHT
                                            else -> DashboardTheme.TEXT_MUTED
                                        }
                                    )
                                    fontSize(24.px)
                                    fontWeight("900")
                                    if (isSelected || isToday) {
                                        property("text-shadow", if (isToday) "0 0 10px rgba(255,47,70,0.72)" else "0 0 10px rgba(255,255,255,0.72)")
                                    }
                                }
                            }) {
                                Text(day.label)
                            }
                        }
                        val dayText = dayTextByDate[day.datum].orEmpty()
                        if (dayText.isNotBlank()) {
                            Span({
                                style {
                                    position(Position.Absolute)
                                    right(10.px)
                                    bottom(8.px)
                                    color(Color(dayTextColorByDate[day.datum] ?: "rgba(255,255,255,0.76)"))
                                    fontSize(15.px)
                                    fontWeight("900")
                                    lineHeight("1.15")
                                    textAlign("right")
                                    property("white-space", "pre-line")
                                }
                            }) { Text(dayText) }
                        } else if (dayMarkerColorByDate[day.datum] != null) {
                            Span({
                                style {
                                    position(Position.Absolute)
                                    right(10.px)
                                    bottom(8.px)
                                    width(17.px)
                                    height(17.px)
                                    borderRadius(50.percent)
                                    backgroundColor(Color(dayMarkerColorByDate[day.datum] ?: "#9AA4B5"))
                                    color(Color.white)
                                    display(DisplayStyle.Flex)
                                    alignItems(AlignItems.Center)
                                    justifyContent(JustifyContent.Center)
                                    fontSize(12.px)
                                    fontWeight("900")
                                    lineHeight("1")
                                    property("box-shadow", "0 0 8px currentColor")
                                }
                            }) { Text(dayMarkerTextByDate[day.datum].orEmpty()) }
                        } else if (dayState.count > 0) {
                            Span({
                                style {
                                    position(Position.Absolute)
                                    right(10.px)
                                    bottom(8.px)
                                    color(Color(dayState.colorHex ?: "#FFFFFF"))
                                    fontSize(12.px)
                                    fontWeight("900")
                                    lineHeight("1")
                                }
                            }) { Text(dayState.count.toString()) }
                        }
                    }
                }
            }
        }
        }
    }

    if (showBookingHover) hoverDay?.let { day ->
        MonatsübersichtHover(day, hoverBookings, hoverX, hoverY, showPrivateBookingDetails)
    }
}

@Composable
private fun MonatsübersichtHover(
    day: All,
    bookings: List<All>,
    clientX: Double,
    clientY: Double,
    showPrivateBookingDetails: Boolean
) {
    Div({
        style {
            position(Position.Fixed)
            left((clientX + 12.0).roundToInt().px)
            top((clientY + 12.0).roundToInt().px)
            width(280.px)
            maxWidth(320.px)
            padding(12.px)
            property("z-index", "70")
            backgroundColor(Color("rgba(15,17,21,0.94)"))
            borderRadius(8.px)
            property("border", "1px solid rgba(255,255,255,0.16)")
            property("box-shadow", "0 28px 80px rgba(0,0,0,0.46)")
            boxSizing("border-box")
            property("pointer-events", "none")
        }
    }) {
        Span({
            style {
                display(DisplayStyle.Block)
                color(DashboardTheme.TEXT_LIGHT)
                fontSize(13.px)
                fontWeight("900")
                marginBottom(8.px)
            }
        }) { Text(formatMonatsDate(day.datum)) }

        if (bookings.isEmpty()) {
            Span({
                style {
                    color(DashboardTheme.TEXT_MUTED)
                    fontSize(12.px)
                    fontWeight("700")
                }
            }) { Text("Keine Termine") }
        } else {
            bookings.take(4).forEach { booking ->
                val isVacation = kalenderBookingIsVacation(booking)
                val timeLabel = when {
                    !showPrivateBookingDetails -> "Zeitbereich nicht frei"
                    isVacation -> kalenderBookingStatusText(booking)
                    booking.schulungHinweis -> "Aktive Schulung"
                    else -> "${booking.uhrzeitVon} - ${booking.uhrzeitBis}"
                }
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        flexDirection(FlexDirection.Column)
                        gap(2.px)
                        padding(7.px, 0.px)
                        property("border-top", "1px solid rgba(255,255,255,0.10)")
                    }
                }) {
                    Span({
                        style {
                            color(DashboardTheme.TEXT_LIGHT)
                            fontSize(12.px)
                            fontWeight("800")
                        }
                    }) { Text(timeLabel) }
                    if (!isVacation && showPrivateBookingDetails) {
                        Span({
                            style {
                                color(DashboardTheme.TEXT_MUTED)
                                fontSize(12.px)
                                fontWeight("700")
                                property("white-space", "nowrap")
                                overflow("hidden")
                                property("text-overflow", "ellipsis")
                            }
                        }) { Text(booking.bezeichnung.ifBlank { booking.art.ifBlank { "Termin" } }) }
                        Span({
                            style {
                                color(DashboardTheme.TEXT_MUTED)
                                fontSize(11.px)
                                fontWeight("600")
                                property("white-space", "nowrap")
                                overflow("hidden")
                                property("text-overflow", "ellipsis")
                            }
                        }) { Text(booking.ort.ifBlank { "-" }) }
                    }
                }
            }
        }
    }
}

private fun formatMonatsDate(value: String): String {
    val parts = value.split("-")
    if (parts.size != 3) return value
    return "${parts[2]}.${parts[1]}.${parts[0]}"
}

private fun monatsübersichtMonthIso(value: String, delta: Int): String {
    val parts = value.split("-")
    val year = parts.getOrNull(0)?.toIntOrNull()
    val month = parts.getOrNull(1)?.toIntOrNull()
    return if (year != null && month != null) {
        kalenderAddMonth(year, month, delta)
    } else {
        value
    }
}
